import { onAuthStateChanged } from "firebase/auth";
import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase-config";
import { useLocation, useNavigate } from "react-router-dom";
import Editor from "../components/Editor";
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, orderBy, query, setDoc, where } from "firebase/firestore";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from '../redux/userReducer'
import { getFirebaseDocs, getUrlParams, randomString } from '../common'
import Header from '../components/Header'
import Input from "../components/Input";
import MyDropzone from "../components/MyDropzone";
import Dropdown from "../components/Dropdown";
import Checkbox from "../components/Checkbox";

const createEmptyCoachQuestion = () => ({
    prompt: '',
    options: ['', ''],
    correctOptionIndex: null
})

const TestingActivity = () => {


    const [confirmDelete, setConfirmDelete] = useState(false)

    const [editId, setEditId] = useState(false)
    const [isAdding, setIsAdding] = useState(false)
    const [name, setName] = useState('')
    const [excerpt, setExcerpt] = useState('')
    const [priority, setPriority] = useState(1)
    const [audio, setAudio] = useState('')
    const [audioTitle, setAudioTitle] = useState('')

    const [question, setQuestion] = useState('')
    const [maxCount, setMaxCount] = useState(10)
    const [passCount, setPassCount] = useState(7)
    const [coachQuestions, setCoachQuestions] = useState([createEmptyCoachQuestion()])

    const [video, setVideo] = useState('')
    const [thumbnail, setThumbnail] = useState('')
    const [relatedLockedActivityId, setRelatedLockedActivityId] = useState('')

    const [loading, setLoading] = useState(false)
    const [mode, setMode] = useState('')
    const [testingType, setTestingType] = useState('parent')
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const location = useLocation()

    const [activities, setActivities] = useState([])

    const userInfo = useSelector(state => state.user.user)


    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                const uid = user.uid
                if (!userInfo) {
                    getUserInfo(uid)
                }
            } else {
                // User is signed out
                navigate('/login')
                // ...
            }
        })
        return () => unsubscribe()
    }, [])

    useEffect(() => {
        if (userInfo && userInfo.role === 'admin') {
            fetchActivity()
        }
    }, [userInfo])

    const getUserInfo = async (uid) => {
        // console.log('uid', uid)
        setLoading(true)
        const userData = await (await getDoc(doc(db, 'users', uid))).data()
        console.log('userData', userData)
        if (!userData) {
            toast.error('User doesn\'t exist. Taking you to login page.')
            setLoading(false)
            setTimeout(async () => {
                await signOutUser()
                navigate('/login')
            }, 3000)
            return
        }
        dispatch(setUser({ ...userData, id: uid }))
    }

    const signOutUser = async () => {
        await auth.signOut();
    }

    const fetchActivity = async () => {
        const params = getUrlParams()
        const localTestingType = params.type === 'coach' ? 'coach' : 'parent'
        setTestingType(localTestingType)

        if (localTestingType === 'coach') {
            setMode('Coach Testing')
            const docs = await getFirebaseDocs(await getDocs(query(collection(db, 'testing-coach-activities'), orderBy('priority', "desc"))))
            setActivities(docs)
            return
        }

        if (!params || !params.subcategory) {
            navigate('/')
            return
        }
        const categoryInfo = await (await getDoc(doc(db, 'testing-subcategories', params.subcategory))).data()
        setMode(categoryInfo.name)
        const docs = await getFirebaseDocs(await getDocs(query(collection(db, 'testing-activities'), where('parentId', '==', params.subcategory), orderBy('priority', "desc"))))
        setActivities(docs)
    }

    const normalizeCoachQuestions = (questions = []) => questions
        .map(questionRow => ({
            prompt: (questionRow.prompt || '').trim(),
            options: (questionRow.options || []).map(option => option.trim()).filter(Boolean).slice(0, 4),
            correctOptionIndex: (() => {
                const directValue = parseInt(questionRow.correctOptionIndex)
                if (!Number.isNaN(directValue) && directValue >= 0 && directValue < 4) {
                    return directValue
                }
                return null
            })()
        }))
        .filter(questionRow => questionRow.prompt)

    const validateCoachQuestions = () => {
        const normalizedQuestions = normalizeCoachQuestions(coachQuestions)

        if (normalizedQuestions.length === 0) {
            toast.error('Add at least one multiple choice question.')
            return false
        }

        for (let questionIndex = 0; questionIndex < normalizedQuestions.length; questionIndex++) {
            const questionRow = normalizedQuestions[questionIndex]

            if (questionRow.options.length < 2) {
                toast.error(`Question ${questionIndex + 1} needs at least 2 options.`)
                return false
            }

            if (questionRow.correctOptionIndex === null) {
                toast.error(`Question ${questionIndex + 1} needs exactly 1 correct option.`)
                return false
            }

            if (questionRow.correctOptionIndex >= questionRow.options.length) {
                toast.error(`Question ${questionIndex + 1} has a correct answer tied to an empty option.`)
                return false
            }
        }

        return true
    }

    const resetForm = () => {
        setConfirmDelete(false)
        setEditId('')
        setName('')
        setExcerpt('')
        setPriority(1)
        setAudio('')
        setAudioTitle('')
        setQuestion('')
        setMaxCount(10)
        setPassCount(7)
        setCoachQuestions([createEmptyCoachQuestion()])
        setVideo('')
        setThumbnail('')
        setRelatedLockedActivityId('')
    }

    const saveCategory = async () => {
        const params = getUrlParams()
        if (testingType === 'coach') {
            if (!validateCoachQuestions()) {
                return
            }

            const payload = {
                name,
                audio: audio || '',
                audioTitle: audioTitle || '',
                excerpt,
                video,
                thumbnail,
                priority: parseInt(priority),
                questions: normalizeCoachQuestions(coachQuestions),
                relatedLockedActivityId,
            }

            if (editId) {
                await setDoc(doc(db, 'testing-coach-activities', editId), payload)
            } else {
                await addDoc(collection(db, 'testing-coach-activities'), payload)
            }

            resetForm()
            setIsAdding(false)
            fetchActivity()
            return
        }

        if (editId) {
            await setDoc(doc(db, 'testing-activities', editId), { name, audio: audio || '', audioTitle: audioTitle || '', parentId: params.subcategory, excerpt, video, thumbnail, priority: parseInt(priority), question, maxCount: parseInt(maxCount), passCount: parseInt(passCount), relatedLockedActivityId })
        } else {
            await addDoc(collection(db, 'testing-activities'), { name, audio: audio || '', audioTitle: audioTitle || '', parentId: params.subcategory, excerpt, video, thumbnail, priority: parseInt(priority), question, maxCount: parseInt(maxCount), passCount: parseInt(passCount), relatedLockedActivityId })
        }
        resetForm()
        setIsAdding(false)
        fetchActivity()
    }

    const goBack = () => {
        const canGoBack = location.key !== 'default';
        if (canGoBack) {
            navigate(-1)
        } else {
            navigate('/testing')
        }
    }


    const deleteActivity = async () => {
        if (editId) {
            if (!confirmDelete) {
                setConfirmDelete(true)
                return
            }
            await deleteDoc(doc(db, testingType === 'coach' ? 'testing-coach-activities' : 'testing-activities', editId))
            setConfirmDelete(false)
            window.location.reload()
        }
    }

    const updateCoachQuestion = (questionIndex, update) => {
        setCoachQuestions(coachQuestions.map((questionRow, idx) => idx === questionIndex ? { ...questionRow, ...update } : questionRow))
    }

    const updateCoachQuestionOption = (questionIndex, optionIndex, nextValue) => {
        setCoachQuestions(coachQuestions.map((questionRow, idx) => {
            if (idx !== questionIndex) {
                return questionRow
            }
            return {
                ...questionRow,
                options: questionRow.options.map((optionValue, idxOption) => idxOption === optionIndex ? nextValue : optionValue)
            }
        }))
    }

    const setCoachQuestionCorrectOption = (questionIndex, optionIndex) => {
        setCoachQuestions(coachQuestions.map((questionRow, idx) => {
            if (idx !== questionIndex) {
                return questionRow
            }
            return {
                ...questionRow,
                correctOptionIndex: questionRow.correctOptionIndex === optionIndex ? null : optionIndex
            }
        }))
    }

    const addCoachQuestion = () => {
        setCoachQuestions([...coachQuestions, createEmptyCoachQuestion()])
    }

    const removeCoachQuestion = (questionIndex) => {
        if (coachQuestions.length === 1) {
            setCoachQuestions([createEmptyCoachQuestion()])
            return
        }
        setCoachQuestions(coachQuestions.filter((_, idx) => idx !== questionIndex))
    }

    const addCoachQuestionOption = (questionIndex) => {
        setCoachQuestions(coachQuestions.map((questionRow, idx) => {
            if (idx !== questionIndex || questionRow.options.length >= 4) {
                return questionRow
            }
            return {
                ...questionRow,
                options: [...questionRow.options, '']
            }
        }))
    }

    const removeCoachQuestionOption = (questionIndex, optionIndex) => {
        setCoachQuestions(coachQuestions.map((questionRow, idx) => {
            if (idx !== questionIndex || questionRow.options.length <= 2) {
                return questionRow
            }

            return {
                ...questionRow,
                options: questionRow.options.filter((_, idxOption) => idxOption !== optionIndex),
                correctOptionIndex: questionRow.correctOptionIndex === optionIndex
                    ? null
                    : (questionRow.correctOptionIndex > optionIndex ? questionRow.correctOptionIndex - 1 : questionRow.correctOptionIndex)
            }
        }))
    }

    const coachLockingOptions = activities
        .filter(act => editId !== act.id && act.priority > priority)
        .map(({ id, name }) => ({ label: name, value: id }))

    return <div className="container">
        <Header />
        {isAdding ? <>
            <h2>{editId ? 'Edit' : 'Add'} a {mode} Activity</h2>
            <Input bold type={'text'} errorMessage={''} errors={[]} name={'name'} value={name} setValue={setName} label={'Name'} />


            <label style={{ display: 'flex', marginTop: '25px', fontWeight: '800', fontSize: '14px', color: '#000', }}>Video</label>
            <MyDropzone allowVideo storageLocation={`Activities/${randomString()}`} customMessage={'Drop file here to upload'} editing={true} errors={[]} name={'video'} image={video} updateImage={(doc) => setVideo(doc)} />

            <label style={{ display: 'flex', marginTop: '25px', fontWeight: '800', fontSize: '14px', color: '#000', }}>Thumbnail</label>
            <MyDropzone storageLocation={`Activities/${randomString()}`} customMessage={'Drop file here to upload'} editing={true} errors={[]} name={'thumbnail'} image={thumbnail} updateImage={(doc) => setThumbnail(doc)} />

            <label style={{ display: 'flex', marginTop: '25px', fontWeight: '800', fontSize: '14px', color: '#000' }}>Audio</label>
            <MyDropzone storageLocation={`Activities/${randomString()}`} allowAudio customMessage={'Drop file here to upload'} editing={true} errors={[]} name={'icon'} image={audio} updateImage={(doc) => setAudio(doc)} />

            <label style={{ display: 'flex', marginTop: '25px', fontWeight: '800', fontSize: '14px', color: '#000' }}>Audio Title</label>
            <MyDropzone storageLocation={`Activities/${randomString()}`} allowAudio customMessage={'Drop file here to upload'} editing={true} errors={[]} name={'icon'} image={audioTitle} updateImage={(doc) => setAudioTitle(doc)} />


            <label style={{ display: 'flex', marginTop: '25px', fontWeight: '800', fontSize: '14px', color: '#000', }}>Excerpt</label>
            <Editor data={excerpt} setData={setExcerpt} />

            <Input bold type={'text'} errorMessage={''} errors={[]} name={'priority'} value={priority} setValue={setPriority} label={'Priority'} />

            {testingType === 'coach' ? <>
                <div style={{ marginTop: '25px' }}>
                    <label style={{ display: 'flex', fontWeight: '800', fontSize: '14px', color: '#000' }}>Questions</label>
                    <p style={{ marginTop: '10px', fontSize: '14px' }}>Add the multiple-choice questions for this video. Coaches must answer every question correctly to unlock the next block.</p>
                    {coachQuestions.map((questionRow, questionIndex) => <div key={`coach-question-${questionIndex}`} style={{ border: '1px solid #ddd', borderRadius: '12px', padding: '16px', marginTop: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <h4 style={{ margin: 0 }}>Question {questionIndex + 1}</h4>
                            <button className="textButton" style={{ marginLeft: 'auto' }} onClick={() => removeCoachQuestion(questionIndex)}>Remove Question</button>
                        </div>
                        <Input bold type={'text'} errorMessage={''} errors={[]} name={`coachQuestion-${questionIndex}`} value={questionRow.prompt} setValue={value => updateCoachQuestion(questionIndex, { prompt: value })} label={'Question Prompt'} />
                        {questionRow.options.map((optionValue, optionIndex) => <div key={`coach-question-${questionIndex}-option-${optionIndex}`} style={{ borderTop: optionIndex === 0 ? 'none' : '1px solid #eee', marginTop: optionIndex === 0 ? '10px' : '16px', paddingTop: optionIndex === 0 ? 0 : '16px' }}>
                            <Input bold type={'text'} errorMessage={''} errors={[]} name={`coachQuestion-${questionIndex}-option-${optionIndex}`} value={optionValue} setValue={value => updateCoachQuestionOption(questionIndex, optionIndex, value)} label={`Option ${String.fromCharCode(65 + optionIndex)}`} />
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Checkbox label={`Mark option ${String.fromCharCode(65 + optionIndex)} as correct`} value={questionRow.correctOptionIndex === optionIndex} setValue={() => setCoachQuestionCorrectOption(questionIndex, optionIndex)} />
                                <button disabled={questionRow.options.length <= 2} className="textButton" style={{ marginLeft: '16px' }} onClick={() => removeCoachQuestionOption(questionIndex, optionIndex)}>Remove Option</button>
                            </div>
                        </div>)}
                        <button disabled={questionRow.options.length >= 4} className="textButton" style={{ marginTop: '16px' }} onClick={() => addCoachQuestionOption(questionIndex)}>+ Add Option</button>
                    </div>)}
                    <button className="textButton" style={{ marginTop: '16px' }} onClick={addCoachQuestion}>+ Add Question</button>
                </div>
            </> : <>
                <Input bold type={'text'} errorMessage={''} errors={[]} name={'question'} value={question} setValue={setQuestion} label={'Question'} />
                <Input bold type={'text'} errorMessage={''} errors={[]} name={'maxCount'} value={maxCount} setValue={setMaxCount} label={'Maximum Count'} />
                <Input bold type={'text'} errorMessage={''} errors={[]} name={'passCount'} value={passCount} setValue={setPassCount} label={'Pass Count'} />
            </>}

            <Dropdown label={testingType === 'coach' ? 'Locked until this coach testing block is completed' : 'Locked until this activity is completed'} options={coachLockingOptions} setValue={v => setRelatedLockedActivityId(v ? v.value : '')} value={activities.find(r => r.id === relatedLockedActivityId) ? ({ label: activities.find(r => r.id === relatedLockedActivityId).name, value: activities.find(r => r.id === relatedLockedActivityId).id }) : ''} placeholder={'Choose activity'} errorFields={[]} clearable />

            <div style={{ width: '200px', margin: '10px auto', display: 'flex', flexDirection: 'column' }}>
                <button disabled={!name || !excerpt || !video || !priority || !parseInt(priority)} onClick={saveCategory}>Save</button>
                <button style={{ margin: '20px auto' }} className="textButton" onClick={() => { resetForm(); setIsAdding(false) }}>Cancel</button>
                <button disabled={!editId} style={{ backgroundColor: 'red' }} onClick={deleteActivity}>{confirmDelete ? 'Confirm Delete' : 'Delete'}</button>
            </div>

        </> : <>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <button className="textButton" style={{ marginLeft: '0' }} onClick={goBack}> {'←Back'}</button>
                <button className="textButton" style={{ marginLeft: 'auto' }} onClick={() => { resetForm(); setIsAdding(true) }}>+ Add New</button>
            </div>

            <h2>{mode} Activities</h2>
            <table>
                <thead>
                    <tr>
                        <th>
                            Name
                        </th>
                        <th>
                            Thumbnail
                        </th>
                        <th>
                            Priority
                        </th>
                        {testingType === 'coach' && <th>
                            Questions
                        </th>}
                        <th>
                            Action
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {activities.map(({ id, name, audio, audioTitle, video, excerpt, priority, thumbnail, question, passCount, maxCount, relatedLockedActivityId, questions = [] }) => <tr key={id}>
                        <td>
                            {name}
                        </td>
                        <td>
                            {thumbnail ? <img alt="" src={thumbnail} style={{ height: '150px' }} /> : '-'}
                        </td>
                        <td>
                            {priority}
                        </td>
                        {testingType === 'coach' && <td>
                            {questions.length}
                        </td>}

                        <td>
                            <div style={{ display: 'flex' }}>
                                {/* <button className="textButton" onClick={() => navigate(`/categories?category=${id}`)}>View</button> */}
                                <button className="textButton" onClick={() => {
                                    setIsAdding(true)
                                    setEditId(id)
                                    setPriority(priority)
                                    setName(name)
                                    setThumbnail(thumbnail)
                                    setVideo(video)
                                    setExcerpt(excerpt)
                                    setQuestion(question || '')
                                    setMaxCount(maxCount || 10)
                                    setPassCount(passCount || 7)
                                    setRelatedLockedActivityId(relatedLockedActivityId || '')
                                    setAudio(audio)
                                    setAudioTitle(audioTitle || '')
                                    setCoachQuestions(questions.length > 0 ? questions.map(questionRow => ({
                                        prompt: questionRow.prompt || '',
                                        options: (questionRow.options || []).slice(0, 4),
                                        correctOptionIndex: typeof questionRow.correctOptionIndex === 'number' ? questionRow.correctOptionIndex : null
                                    })) : [createEmptyCoachQuestion()])
                                }}>Edit</button>
                            </div>
                        </td>

                    </tr>)}
                </tbody>
            </table>
        </>}
    </div>
}

export default TestingActivity
